/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IComponentProps, ICoreContext, IDateFormatOptions, msdyn365Commerce, isOboRequest } from '@msdyn365-commerce/core';
import { ArrayExtensions, StringExtensions } from '@msdyn365-commerce-modules/retail-actions';
import { ChannelIdentity, Customer, OrderOriginator, OrgUnitLocation, SalesOrder } from '@msdyn365-commerce/retail-proxy';
import {
    ActionMenuComponent,
    TableCellComponent,
    TableComponent,
    TableCellContentType,
    TableHeaderComponent,
    TableRowComponent
} from '@msdyn365-commerce/components';
import React from 'react';

import classnames from 'classnames';

import {
    Button,
    ITelemetryContent,
    TelemetryConstant,
    getPayloadObject,
    getTelemetryAttributes
} from '@msdyn365-commerce-modules/utilities';
import { countItems, getOrderChannelAddress, getOrderChannelName, getOrderChannelTypeValue, isOnlineStoreChannel } from '../../../common';
import {
    IOrderHistoryWithSearchAndFiltersOrderStatusResources,
    getOrderDetailsPageUrl,
    getOrderStatusLabel
} from '../common/get-sales-order';

/**
 * Class names used in component.
 */
const componentClassName = 'ms-order-history-with-search-and-filters-order-table';
const placedByClassName = `${componentClassName}__row__placed-by`;
const orderNumberClassName = `${componentClassName}__row__order-number`;
const columnMobileClassName = `${componentClassName}__row__mobile-cell`;
const actionsClassName = `${componentClassName}__row__actions`;
const viewDetailsMenuItemClassName = `${componentClassName}__row__view-details-action`;

/**
 * IOrderHistoryWithSearchAndFiltersOrderTableResources interface.
 */
export interface IOrderHistoryWithSearchAndFiltersOrderTableResources extends IOrderHistoryWithSearchAndFiltersOrderStatusResources {
    orderHistoryOrderNumberText: string;
    orderHistoryCreatedDateText: string;
    orderHistoryItemsText: string;
    orderHistoryTotalText: string;
    orderHistoryOriginText: string;
    orderHistoryOrderStatusText: string;
    orderHistoryActionMenuButtonAriaLabel: string;
    orderHistoryViewDetailsButtonText: string;
    orderHistoryViewDetailsButtonAriaLabel: string;
    orderHistoryCreatedDateMobileDescriptionText: string;
    orderHistoryOrderNumberIsNotAvailable: string;
    onlineStoreChannelNameText: string;
    posChannelNameText: string;
    orderPlacedByFullText?: string;
    orderPlacedByYouText?: string;
    orderOnBehalfOfText?: string;
}

/**
 * IOrderHistoryWithSearchAndFiltersOrderTableData interface.
 */
export interface IOrderHistoryWithSearchAndFiltersOrderTableData {
    salesOrders: SalesOrder[];
    channelIdentities?: ChannelIdentity[];
    orgUnitLocations?: OrgUnitLocation[];
    customer?: Customer;
    originators?: OrderOriginator[];
}

/**
 * IOrderHistoryWithSearchAndFiltersOrderTableProps interface.
 */
export interface IOrderHistoryWithSearchAndFiltersOrderTableProps extends IComponentProps<IOrderHistoryWithSearchAndFiltersOrderTableData> {
    className?: string;
    resources: IOrderHistoryWithSearchAndFiltersOrderTableResources;
    isMobileView?: boolean;
    showChannelInfo: boolean;
    showOrderPlacedBy?: boolean;
    telemetryContent?: ITelemetryContent;
}

interface IOnBehalfOfOrderResources {
    orderHistoryOrderNumberIsNotAvailable?: string;
}

interface IOnBehalfOfOrderProps {
    salesOrder: SalesOrder;
    isOnBehalfOfOrder: boolean;
    originator?: OrderOriginator;
    placedByName: string;
    placedByText: string;
    onBehalfOfText: string;
    showOrderPlacedBy?: boolean;
    resources: IOnBehalfOfOrderResources;
}

/**
 * Formats amount.
 * @param context - Core context.
 * @param amount - Amount to display.
 * @param currencyCode - Currency for the amount.
 * @returns String value.
 */
const formatAmount = (context: ICoreContext, amount: number | undefined, currencyCode: string | undefined) => {
    if (amount === undefined) {
        return '';
    }
    return context.cultureFormatter.formatCurrency(amount, currencyCode);
};

/**
 * Formats date.
 * @param context - Core context.
 * @param date - Date to display.
 * @returns String value.
 */
const formatDate = (context: ICoreContext, date: Date | undefined) => {
    if (date === undefined) {
        return '';
    }

    const dateOptions: IDateFormatOptions = { year: 'numeric', month: 'short', day: 'numeric' };
    return context.cultureFormatter.formatDate(date, dateOptions);
};

const getShortEllipsisText = (originalText: string, maxTextLength: number) => {
    const isRtl = document.body.parentElement?.getAttribute('dir') === 'rtl';
    return originalText.length > maxTextLength
        ? isRtl
            ? `…${originalText.substring(0, maxTextLength - 1)}`
            : `${originalText.substring(0, maxTextLength - 1)}…`
        : originalText;
};

const getOnBehalfOfOrderProps = (
    props: IOrderHistoryWithSearchAndFiltersOrderTableProps,
    salesOrder: SalesOrder
): IOnBehalfOfOrderProps => {
    const {
        data: { channelIdentities, orgUnitLocations, customer, originators },
        resources: { orderPlacedByFullText, orderPlacedByYouText, orderOnBehalfOfText, orderHistoryOrderNumberIsNotAvailable }
    } = props;

    const channelAddress = getOrderChannelAddress(salesOrder, orgUnitLocations);
    const channelTypeValue = getOrderChannelTypeValue(salesOrder, channelIdentities);
    const isOnlineStore = isOnlineStoreChannel(channelTypeValue, channelAddress);

    const originator: OrderOriginator | undefined = originators?.find(
        originator =>
            (salesOrder.SalesId && originator.SalesId === salesOrder.SalesId) ||
            (salesOrder.Id && originator.TransactionId === salesOrder.Id)
    );

    const isYou =
        !isOboRequest(props.context.request) &&
        originator?.CustomerId &&
        originator?.CustomerName &&
        !originator?.StaffId &&
        !originator?.StaffName &&
        customer?.AccountNumber === originator?.CustomerId
            ? true
            : false;
    const isYouObo = !isYou && !isOboRequest(props.context.request);
    let placedByName =
        originator?.StaffId && originator.StaffName
            ? originator.StaffName
            : originator?.CustomerId && originator.CustomerName
            ? originator.CustomerName
            : '-';
    placedByName = `${placedByName} ${isYou ? orderPlacedByYouText : ''}`;
    const isOnBehalfOfOrder = isOnlineStore && originator?.StaffName && customer?.Name;
    const placedByText = `${orderPlacedByFullText} ${placedByName}`;
    const onBehalfOfText = `${orderOnBehalfOfText} ${customer?.Name} ${isYouObo ? orderPlacedByYouText : ''}`;

    return {
        resources: { orderHistoryOrderNumberIsNotAvailable: orderHistoryOrderNumberIsNotAvailable },
        salesOrder: salesOrder,
        isOnBehalfOfOrder: !!isOnBehalfOfOrder,
        originator: originator,
        placedByName: placedByName,
        placedByText: placedByText,
        onBehalfOfText: onBehalfOfText,
        showOrderPlacedBy: props.showOrderPlacedBy
    };
};

const getOrderPlacedByCellContent = (props: IOnBehalfOfOrderProps): JSX.Element | null => {
    const maxOrderTextLength = window.innerWidth > 700 ? 20 : 12;
    return (
        <span className={placedByClassName} data-title={`${props.placedByName} ${props.isOnBehalfOfOrder ? props.onBehalfOfText : ''}`}>
            {props.placedByName}
            {props.isOnBehalfOfOrder && (
                <>
                    <br /> {getShortEllipsisText(props.onBehalfOfText, maxOrderTextLength)}
                </>
            )}
        </span>
    );
};

const getOrderNumberCellContent = (onBehalfOfOrderProps: IOnBehalfOfOrderProps, linkToOrderDetails: string): JSX.Element | null => {
    const maxOrderTextLength = window.innerWidth > 700 ? 20 : 12;
    const {
        salesOrder,
        isOnBehalfOfOrder,
        placedByText,
        showOrderPlacedBy,
        resources: { orderHistoryOrderNumberIsNotAvailable }
    } = onBehalfOfOrderProps;

    let orderNumberCellText = salesOrder.SalesId;
    if (StringExtensions.isNullOrWhitespace(orderNumberCellText)) {
        orderNumberCellText = orderHistoryOrderNumberIsNotAvailable;
    }

    return (
        <>
            <Button className={orderNumberClassName} href={linkToOrderDetails ?? ''}>
                {orderNumberCellText}
            </Button>
            {isOnBehalfOfOrder && !showOrderPlacedBy && (
                <span className={placedByClassName} data-title={placedByText}>
                    {getShortEllipsisText(placedByText, maxOrderTextLength)}
                </span>
            )}
        </>
    );
};

const getTableRowComponentChildren = (
    props: IOrderHistoryWithSearchAndFiltersOrderTableProps,
    index: number,
    salesOrder: SalesOrder
): React.ReactElement[] => {
    const numItemsCellText: number = ArrayExtensions.hasElements(salesOrder.SalesLines) ? countItems(salesOrder.SalesLines) : 0;
    const totalAmountCellText: string = formatAmount(props.context, salesOrder.TotalAmount, salesOrder.CurrencyCode);

    const orderStatusCellText: string = getOrderStatusLabel(salesOrder.StatusValue, props.resources);

    const channelName: string = getOrderChannelName(salesOrder, props.data.channelIdentities);
    const channelAddress: string = getOrderChannelAddress(salesOrder, props.data.orgUnitLocations);
    const channelNameCellText: string =
        channelName &&
        (channelAddress ? `${props.resources.posChannelNameText} ${channelName}` : props.resources.onlineStoreChannelNameText);

    const onBehalfOfOrderProps: IOnBehalfOfOrderProps = getOnBehalfOfOrderProps(props, salesOrder);

    const linkToOrderDetails = getOrderDetailsPageUrl(props.context.actionContext, salesOrder);

    const payLoad = getPayloadObject('click', props.telemetryContent!, TelemetryConstant.OrderDetails);
    const orderDetailsAttributes = getTelemetryAttributes(props.telemetryContent!, payLoad);

    return [
        <TableCellComponent
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            data={{}}
            key={`row_${index}_0`}
            contentType={TableCellContentType.Text}
            isVisible={!!props.showOrderPlacedBy && !props.isMobileView}
        >
            {getOrderPlacedByCellContent(onBehalfOfOrderProps)}
        </TableCellComponent>,
        <TableCellComponent
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            data={{}}
            key={`row_${index}_1`}
            contentType={TableCellContentType.Text}
            isVisible={true}
        >
            {getOrderNumberCellContent(onBehalfOfOrderProps, linkToOrderDetails)}
        </TableCellComponent>,
        <TableCellComponent
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            data={{}}
            key={`row_${index}_2`}
            contentType={TableCellContentType.Text}
            isVisible={!!props.showChannelInfo && !props.isMobileView}
        >
            {channelNameCellText}
        </TableCellComponent>,
        <TableCellComponent
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            data={{}}
            key={`row_${index}_3`}
            contentType={TableCellContentType.Number}
            isVisible={!props.isMobileView}
        >
            {numItemsCellText}
        </TableCellComponent>,
        <TableCellComponent
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            data={{}}
            key={`row_${index}_4`}
            contentType={TableCellContentType.Amount}
            isVisible={!props.isMobileView}
        >
            {totalAmountCellText}
        </TableCellComponent>,
        <TableCellComponent
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            data={{}}
            key={`row_${index}_5`}
            contentType={TableCellContentType.Date}
            isVisible={!props.isMobileView}
        >
            {formatDate(props.context, salesOrder.CreatedDateTime)}
        </TableCellComponent>,
        <TableCellComponent
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            data={{}}
            key={`row_${index}_6`}
            contentType={TableCellContentType.Text}
            isVisible={true}
        >
            {orderStatusCellText}
        </TableCellComponent>,
        <TableCellComponent
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            data={{}}
            key={`row_${index}_7`}
            className={classnames(actionsClassName)}
            contentType={TableCellContentType.Text}
            isVisible={true}
        >
            <ActionMenuComponent
                context={props.context}
                id={props.id}
                typeName={props.typeName}
                data={{}}
                ariaLabel={props.resources.orderHistoryActionMenuButtonAriaLabel}
            >
                <Button
                    className={viewDetailsMenuItemClassName}
                    href={linkToOrderDetails ?? ''}
                    title={props.resources.orderHistoryViewDetailsButtonText ?? ''}
                    aria-label={props.resources.orderHistoryViewDetailsButtonAriaLabel ?? props.resources.orderHistoryViewDetailsButtonText}
                    {...orderDetailsAttributes}
                >
                    {props.resources.orderHistoryViewDetailsButtonText}
                </Button>
            </ActionMenuComponent>
        </TableCellComponent>
    ];
};

/**
 * OrderHistoryWithSearchAndFiltersOrderTable component.
 * @param props - Configuration of the component.
 * @returns React node.
 */
const OrderHistoryWithSearchAndFiltersOrderTableFunctionalComponent: React.FC<IOrderHistoryWithSearchAndFiltersOrderTableProps> = (
    props: IOrderHistoryWithSearchAndFiltersOrderTableProps
) => {
    return (
        <TableComponent
            context={props.context}
            id={props.id}
            typeName={props.typeName}
            data={{}}
            className={classnames(props.className, componentClassName)}
        >
            <TableHeaderComponent context={props.context} id={props.id} typeName={props.typeName} data={{}}>
                <TableCellComponent
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    data={{}}
                    contentType={TableCellContentType.Text}
                    isVisible={!!props.showOrderPlacedBy && !props.isMobileView}
                >
                    {props.resources.orderPlacedByFullText ?? ''}
                </TableCellComponent>
                <TableCellComponent
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    data={{}}
                    className={classnames(props.isMobileView ? columnMobileClassName : '')}
                    contentType={TableCellContentType.Text}
                    isVisible={true}
                >
                    {props.resources.orderHistoryOrderNumberText}
                </TableCellComponent>
                <TableCellComponent
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    data={{}}
                    contentType={TableCellContentType.Text}
                    isVisible={!!props.showChannelInfo && !props.isMobileView}
                >
                    {props.resources.orderHistoryOriginText}
                </TableCellComponent>
                <TableCellComponent
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    data={{}}
                    contentType={TableCellContentType.Number}
                    isVisible={!props.isMobileView}
                >
                    {props.resources.orderHistoryItemsText}
                </TableCellComponent>
                <TableCellComponent
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    data={{}}
                    contentType={TableCellContentType.Amount}
                    isVisible={!props.isMobileView}
                >
                    {props.resources.orderHistoryTotalText}
                </TableCellComponent>
                <TableCellComponent
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    data={{}}
                    contentType={TableCellContentType.Date}
                    isVisible={!props.isMobileView}
                >
                    {props.resources.orderHistoryCreatedDateText}
                </TableCellComponent>
                <TableCellComponent
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    data={{}}
                    contentType={TableCellContentType.Text}
                    isVisible={true}
                >
                    {props.resources.orderHistoryOrderStatusText}
                </TableCellComponent>
                <TableCellComponent
                    context={props.context}
                    id={props.id}
                    typeName={props.typeName}
                    data={{}}
                    contentType={TableCellContentType.Text}
                    isVisible={true}
                />
            </TableHeaderComponent>
            {props.data.salesOrders?.map((salesOrder: SalesOrder, index: number) => {
                return (
                    <TableRowComponent context={props.context} id={props.id} typeName={props.typeName} data={{}} key={`row_${index}`}>
                        {getTableRowComponentChildren(props, index, salesOrder)}
                    </TableRowComponent>
                );
            })}
        </TableComponent>
    );
};

/**
 * OrderHistoryWithSearchAndFiltersOrderTable component.
 */
export const OrderHistoryWithSearchAndFiltersOrderTableComponent: React.FunctionComponent<IOrderHistoryWithSearchAndFiltersOrderTableProps> = msdyn365Commerce.createComponent<
    // @ts-expect-error
    IOrderHistoryWithSearchAndFiltersOrderTableProps
>('OrderHistoryWithSearchAndFiltersOrderTableComponent', { component: OrderHistoryWithSearchAndFiltersOrderTableFunctionalComponent });
